import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { GetApp, KeyboardArrowDown, KeyboardArrowRight, CloudUpload } from '@mui/icons-material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import toast from 'react-hot-toast';
import { frsApi } from 'src/api/frs';
import { frsCategoriesApi } from 'src/api/frsCategories';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import FrsBulkUploadModal from './FrsBulkUploadModal';

import {
  FrsCategory,
  FrsItem,
  AddDraftState,
} from './frsTypes';
import {
  GATE_COLORS,
  MOSCOW_COLORS,
  MOSCOW_OPTIONS,
  DEFAULT_GATE_COLOR,
  toCategoryNumber,
  extractDataArray,
  GATE_OPTIONS,
  DEFAULT_MOSCOW_COLOR,
  formatMoscowLabel,
  MOSCOW_FIELD_LABEL,
  triggerBrowserDownload,
} from './frsUtils';

const normalizeFrs = (item: any): FrsItem => ({
  _id: String(item?._id || item?.id || item?.frsId || item?.title || Math.random()),
  frsId: String(item?.frsId || item?.id || item?.code || item?.number || '-'),
  title: String(item?.title || ''),
  gate: String(item?.gate || 'Planned'),
  moscow: String(item?.moscow || 'Must have'),
  frsDisplayKey: String(item?.frsDisplayKey || ''),
  description: String(item?.description || ''),
  categoryId: String(item?.categoryId || ''),
  approvalStatus: Boolean(item?.approvalStatus),
});

const normalizeCategory = (item: any): FrsCategory => {
  const categoryId = String(item?._id || item?.id || item?.categoryId || item?.name || Math.random());
  return {
    _id: categoryId,
    categoryId: toCategoryNumber(item?.categoryPath ?? item?.categoryId ?? item?.code),
    name: String(item?.name || ''),
    children: Array.isArray(item?.children)
      ? item.children.map((child) => normalizeCategory(child))
      : Array.isArray(item?.subCategories)
        ? item.subCategories.map((child) => normalizeCategory(child))
        : [],
    frsItems: Array.isArray(item?.frs)
      ? item.frs.map((frs) => normalizeFrs({ ...frs, categoryId }))
      : Array.isArray(item?.frsItems)
        ? item.frsItems.map((frs) => normalizeFrs({ ...frs, categoryId }))
        : [],
  };
};

const buildCategoryTree = (raw: any[]): FrsCategory[] => {
  const items = raw.map((item) => normalizeCategory(item));
  const hasNested = items.some((item) => item.children.length > 0);

  if (hasNested) return items;

  const map = new Map<string, FrsCategory>();
  const roots: FrsCategory[] = [];

  // First pass: Initialize map
  raw.forEach((rawItem) => {
    const item = normalizeCategory(rawItem);
    map.set(item._id, item);
  });

  // Second pass: Build hierarchy
  raw.forEach((rawItem) => {
    const id = String(rawItem?._id || rawItem?.id || rawItem?.categoryId || rawItem?.name);
    const parentId = rawItem?.parentCategoryId || rawItem?.parentId;
    const node = map.get(id);

    if (node) {
      if (parentId && map.has(parentId)) {
        map.get(parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  return roots;
};



const FRS: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { projectID } = useParams<{ projectID: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<FrsCategory[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState<AddDraftState | null>(null);

  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState<FrsCategory | null>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  const [anchorElGate, setAnchorElGate] = useState<{ element: HTMLElement; frs: FrsItem } | null>(null);
  const [anchorElMoscow, setAnchorElMoscow] = useState<{ element: HTMLElement; frs: FrsItem } | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchHierarchy = useCallback(async () => {
    if (!projectID) {
      setCategories([]);
      return;
    }

    try {
      setError('');
      setIsLoading(true);
      const response = await frsApi.getFrsHierarchy({ projectId: projectID });
      const tree = buildCategoryTree(extractDataArray(response));
      setCategories(tree);
      setExpanded((prev) => {
        const next = { ...prev };
        tree.forEach((node) => {
          if (next[node._id] === undefined) {
            next[node._id] = true;
          }
        });
        return next;
      });
    } catch (_error) {
      setError('Failed to load FRS hierarchy.');
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectID]);

  useEffect(() => {
    fetchHierarchy();
  }, [fetchHierarchy]);

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));
  }, []);

  const handleCreateFrs = useCallback(async () => {
    if (!projectID || !draft || !draft.title.trim()) {
      return;
    }

    try {
      setIsCreating(true);
      await frsApi.createFrs({
        approvalStatus: false,
        categoryId: draft.categoryId,
        description: '',
        gate: draft.gate,
        moscow: draft.moscow,
        projectId: projectID,
        title: draft.title.trim(),
      });
      setDraft(null);
      fetchHierarchy();
    } catch (_error) {
      setError('Failed to create FRS.');
    } finally {
      setIsCreating(false);
    }
  }, [projectID, draft, fetchHierarchy]);

  const closeAddCategoryDialog = useCallback(() => {
    setIsAddCategoryDialogOpen(false);
    setCategoryName('');
    setSelectedParentCategory(null);
  }, []);

  const handleCreateCategory = useCallback(async () => {
    if (!projectID || !categoryName.trim()) {
      return;
    }

    try {
      setIsCreatingCategory(true);
      await frsCategoriesApi.createFrsCategory({
        projectId: projectID,
        name: categoryName.trim(),
        ...(selectedParentCategory?._id ? { parentCategoryId: selectedParentCategory._id } : {}),
      });
      closeAddCategoryDialog();
      fetchHierarchy();
    } catch (_error) {
      setError('Failed to create FRS category.');
    } finally {
      setIsCreatingCategory(false);
    }
  }, [projectID, categoryName, selectedParentCategory, closeAddCategoryDialog, fetchHierarchy]);

  const handleUpdateGate = useCallback(async (gate: string) => {
    if (!anchorElGate) return;
    const { frs } = anchorElGate;
    setAnchorElGate(null);
    if (frs.gate === gate) return;

    try {
      await frsApi.updateFrs(frs._id, {
        projectId: projectID,
        categoryId: frs.categoryId,
        title: frs.title,
        description: frs.description,
        gate,
        moscow: frs.moscow,
        approvalStatus: frs.approvalStatus,
      });
      fetchHierarchy();
    } catch (err) {
      setError('Failed to update Gate.');
    }
  }, [anchorElGate, projectID, fetchHierarchy]);

  const handleUpdateMoscow = useCallback(async (moscow: string) => {
    if (!anchorElMoscow) return;
    const { frs } = anchorElMoscow;
    setAnchorElMoscow(null);
    if (frs.moscow === moscow) return;

    try {
      await frsApi.updateFrs(frs._id, {
        projectId: projectID,
        categoryId: frs.categoryId,
        title: frs.title,
        description: frs.description,
        gate: frs.gate,
        moscow,
        approvalStatus: frs.approvalStatus,
      });
      fetchHierarchy();
    } catch (err) {
      setError(`Failed to update ${MOSCOW_FIELD_LABEL}.`);
    }
  }, [anchorElMoscow, projectID, fetchHierarchy]);

  const handleRowClick = useCallback(
    (frsId: string) => {
      if (!projectID) return;
      navigate(`/projects/${projectID}/frs/${frsId}`);
    },
    [navigate, projectID]
  );

  const handleExport = useCallback(async () => {
    if (!projectID) return;
    try {
      setIsExporting(true);
      toast.loading('Exporting FRS data...', { id: 'frs-export' });
      const { blob, filename } = await frsApi.exportBulkImport(projectID);
      triggerBrowserDownload(blob, filename);
      toast.success('Export downloaded', { id: 'frs-export' });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to export FRS data', { id: 'frs-export' });
    } finally {
      setIsExporting(false);
    }
  }, [projectID]);

  const flattenedData = useMemo(() => {
    const list: any[] = [];
    const flatten = (nodes: FrsCategory[], level = 0) => {
      nodes.forEach((category) => {
        const isExpanded = expanded[category._id] ?? true;
        list.push({ ...category, type: 'category', level, id: category._id });

        if (isExpanded) {
          category.frsItems.forEach((frs) => {
            list.push({ ...frs, type: 'frs', level: level + 1, id: frs._id, categoryId: category._id });
          });

          if (draft?.categoryId === category._id) {
            list.push({ type: 'draft', level: level + 1, id: 'draft', categoryId: category._id, title: draft.title, gate: draft.gate, moscow: draft.moscow });
          }

          if (category.children.length > 0) {
            flatten(category.children, level + 1);
          }
        }
      });
    };
    flatten(categories);
    return list;
  }, [categories, expanded, draft]);

  const columns = useMemo(() => [
    {
      field: 'idColumn',
      headerName: (
        <Box display="flex" gap={2} alignItems="center">
          Category ID / FRS ID
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsAddCategoryDialogOpen(true);
            }}
            style={{ textTransform: 'none', minWidth: 'unset', padding: '4px 12px', fontSize: '0.75rem' }}
            variant="outlined"
            size="small"
            color="primary">
            + Add Category
          </Button>
        </Box>
      ),
      disableTooltip: true,
      width: '25%',
      render: (_: any, row: any) => {
        if (row.type === 'category') {
          const hasChildren = row.children?.length > 0 || row.frsItems?.length > 0 || draft?.categoryId === row._id;
          const isExpanded = expanded[row._id] ?? true;
          return (
            <Box display="flex" alignItems="center" pl={row.level * 3}>
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(row._id);
                  }}>
                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                </IconButton>
              ) : (
                <Box width={32} />
              )}
              <Typography variant="body2" color="textPrimary" style={{ fontSize: '0.75rem' }}>
                {row.categoryId || '-'}
              </Typography>
              <Box
                className="row-actions"
                display="flex"
                alignItems="center"
                sx={{
                  opacity: 0,
                  transition: () => theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
                }}>
                <Button
                  className="add-subcategory-button"
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => ({ ...prev, [row._id]: true }));
                    setSelectedParentCategory(row);
                    setIsAddCategoryDialogOpen(true);
                  }}
                  style={{
                    textTransform: 'none',
                    minWidth: 'unset',
                    marginLeft: '12px',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                  }}
                  color="primary">
                  + Category
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => ({ ...prev, [row._id]: true }));
                    setDraft({
                      categoryId: row._id,
                      title: '',
                      gate: 'Planned',
                      moscow: 'Must have',
                    });
                  }}
                  color="primary"
                  style={{ marginLeft: '12px', textTransform: 'none', minWidth: 'unset', padding: '2px 8px', fontSize: '0.75rem' }}>
                  + FRS
                </Button>
              </Box>
            </Box>
          );
        }
        if (row.type === 'frs') {
          return (
            <Box pl={2 + row.level * 3}>
              <Typography variant="body2" color="primary" style={{ fontSize: '0.75rem' }}>
                {row.frsDisplayKey}
              </Typography>
            </Box>
          );
        }
        if (row.type === 'draft') {
          return (
            <Box pl={2 + row.level * 3}>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                New FRS Draft
              </Typography>
            </Box>
          );
        }
        return null;
      }
    },
    {
      field: 'titleColumn',
      headerName: "Title",
      width: '35%',
      render: (_: any, row: any) => {
        if (row.type === 'category') {
          return (
            <Typography variant="body2" color="textPrimary" style={{ fontSize: '0.75rem' }}>
              {row.name}
            </Typography>
          );
        }
        if (row.type === 'frs') {
          return (
            <Typography variant="body2" color="textPrimary" style={{ fontSize: '0.75rem' }}>
              {row.title}
            </Typography>
          );
        }
        if (row.type === 'draft') {
          return (
            <TextField
              value={draft?.title || ''}
              onChange={(event) =>
                setDraft((prev) =>
                  prev ? { ...prev, title: event.target.value } : prev
                )
              }
              onClick={(e) => e.stopPropagation()}
              placeholder="Title"
              size="small"
              fullWidth
              variant="outlined"
            />
          );
        }
        return null;
      }
    },
    {
      field: 'gateColumn',
      headerName: "Gate",
      width: '20%',
      render: (value: any, row: any) => {
        if (row.type === 'frs') {
          return (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setAnchorElGate({ element: e.currentTarget, frs: row });
              }}
              sx={{
                cursor: 'pointer',
                display: 'inline-block',
                '&:hover': { opacity: 0.8 }
              }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: GATE_COLORS[row.gate] || DEFAULT_GATE_COLOR,
                  }}
                />
                <Typography variant="body2" style={{ color: GATE_COLORS[row.gate] || DEFAULT_GATE_COLOR, fontWeight: 500, fontSize: '0.75rem' }}>
                  {row.gate}
                </Typography>
              </Box>
            </Box>
          );
        }
        if (row.type === 'draft') {
          return (
            <TextField
              select
              value={draft?.gate || ''}
              onChange={(event) =>
                setDraft((prev) =>
                  prev ? { ...prev, gate: event.target.value } : prev
                )
              }
              onClick={(e) => e.stopPropagation()}
              size="small"
              fullWidth
              variant="outlined">
              {GATE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        return null;
      }
    },
    {
      field: 'moscowColumn',
      headerName: MOSCOW_FIELD_LABEL,
      width: '15%',
      render: (_: any, row: any) => {
        if (row.type === 'frs') {
          return (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setAnchorElMoscow({ element: e.currentTarget, frs: row });
              }}
              sx={{
                cursor: 'pointer',
                display: 'inline-block',
                bgcolor: (MOSCOW_COLORS[row.moscow] || DEFAULT_MOSCOW_COLOR).bg,
                color: (MOSCOW_COLORS[row.moscow] || DEFAULT_MOSCOW_COLOR).text,
                px: 1.5,
                py: 0.5,
                borderRadius: 16,
                '&:hover': { opacity: 0.8 }
              }}>
              <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {formatMoscowLabel(row.moscow)}
              </Typography>
            </Box>
          );
        }
        if (row.type === 'draft') {
          return (
            <TextField
              select
              value={draft?.moscow || ''}
              onChange={(event) =>
                setDraft((prev) =>
                  prev ? { ...prev, moscow: event.target.value } : prev
                )
              }
              onClick={(e) => e.stopPropagation()}
              size="small"
              fullWidth
              variant="outlined">
              {MOSCOW_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        return null;
      }
    },
    {
      field: 'actionsColumn',
      headerName: '',
      width: '5%',
      render: (value: any, row: any) => {
        if (row.type === 'draft') {
          return (
            <Box display="flex" justifyContent="center" gap={1}>
              <Button
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateFrs();
                }}
                disabled={!draft?.title.trim() || isCreating}>
                ✓
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDraft(null);
                }}
                disabled={isCreating}>
                ✕
              </Button>
            </Box>
          );
        }
        return null;
      }
    }
  ], [draft, expanded, theme, isCreating, handleCreateFrs, handleToggle]);

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        minHeight={0}
        height="100%"
        overflow="hidden">
        <Box
          flexShrink={0}
          display="flex"
          justifyContent="flex-end"
          gap={1}
          mb={2}
          px={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={isExporting ? <CircularProgress size={16} /> : <GetApp />}
            onClick={handleExport}
            disabled={!projectID || isExporting}
            style={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Export
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CloudUpload />}
            onClick={() => setIsBulkUploadOpen(true)}
            disabled={!projectID}
            style={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Bulk Upload
          </Button>
        </Box>

        <CustomizedTable
          columns={columns}
          data={flattenedData}
          loading={isLoading}
          showPagination={false}
          maxHeight="100%"
          getRowStyle={useCallback((row) => {
            if (row.type === 'frs') {
              return { backgroundColor: theme.palette.primary.main + '0D' }; // Light primary background
            }
            return {};
          }, [theme])}
          handleOnClick={useCallback((row) => {
            if (row.type === 'category') {
              handleToggle(row._id);
            } else if (row.type === 'frs') {
              handleRowClick(row.frsId);
            }
          }, [handleToggle, handleRowClick])}
        />
      </Box>

      {error && (
        <Box
          px={2}
          py={1}
          bgcolor={theme.palette.error.light}
          borderTop={`1px solid ${theme.palette.error.main}`}>
          <Typography variant="body2" style={{ color: theme.palette.error.main }}>
            {error}
          </Typography>
        </Box>
      )}

      <Dialog open={isAddCategoryDialogOpen} onClose={closeAddCategoryDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {selectedParentCategory ? `Add Category to ${selectedParentCategory.name}` : 'Add Category'}
        </DialogTitle>
        <DialogContent>
          <TextField
            value={categoryName}
            onChange={(event) => setCategoryName(event.target.value)}
            margin="dense"
            fullWidth
            label="Category name"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddCategoryDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            color="primary"
            variant="contained"
            disabled={!categoryName.trim() || isCreatingCategory}>
            {isCreatingCategory ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorElGate?.element}
        open={Boolean(anchorElGate)}
        onClose={() => setAnchorElGate(null)}
      >
        {GATE_OPTIONS.map((option) => {
          const currentGate = anchorElGate?.frs.gate || '';
          const currentIndex = GATE_OPTIONS.indexOf(currentGate);
          const optionIndex = GATE_OPTIONS.indexOf(option);
          // Allow only moving forward by exactly one step, or staying on the current step.
          const isDisabled = currentIndex !== -1 && (optionIndex < currentIndex || optionIndex > currentIndex + 1);

          const isCurrent = option === currentGate;

          return (
            <MenuItem
              key={option}
              disabled={isDisabled}
              onClick={() => handleUpdateGate(option)}
              style={{
                color: isCurrent ? theme.palette.text.primary : isDisabled ? '#bdbdbd' : '#757575',
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              {option}
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        anchorEl={anchorElMoscow?.element}
        open={Boolean(anchorElMoscow)}
        onClose={() => setAnchorElMoscow(null)}
      >
        {MOSCOW_OPTIONS.map((option) => (
          <MenuItem key={option} onClick={() => handleUpdateMoscow(option)}>
            {option}
          </MenuItem>
        ))}
      </Menu>

      <FrsBulkUploadModal
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        projectId={projectID || ''}
        onImportSuccess={fetchHierarchy}
      />
    </>
  );
};

export default FRS;